import React from 'react';
import { Calculator, TrendingUp } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров калькулятора инфляции
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  inflationRatesByYear
}) => {
  // Расчет текущей суммы
  const amount = parseFloat(formData.amount) || 0;
  
  // Получаем минимальный и максимальный год из доступных данных
  const years = Object.keys(inflationRatesByYear).map(Number);
  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);
  const currentYear = new Date().getFullYear();
  
  // Обработчик изменения суммы
  const handleAmountChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      amount: newValue
    }));
  };
  
  // Обработчик изменения начального года
  const handleStartYearChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      startYear: newValue
    }));
  };
  
  // Обработчик изменения конечного года
  const handleEndYearChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      endYear: newValue
    }));
  };
  
  // Обработчик изменения пользовательской ставки инфляции (если включена)
  const handleCustomRateChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      customInflationRate: newValue
    }));
  };
  
  // Предопределенные суммы для быстрого выбора
  const predefinedAmounts = [
    { value: '10000', label: '10 тыс.' },
    { value: '100000', label: '100 тыс.' },
    { value: '1000000', label: '1 млн' },
    { value: '10000000', label: '10 млн' }
  ];
  
  // Предопределенные периоды для быстрого выбора
  const predefinedPeriods = [
    { start: currentYear - 1, end: currentYear, label: '1 год назад' },
    { start: currentYear - 5, end: currentYear, label: '5 лет назад' },
    { start: currentYear - 10, end: currentYear, label: '10 лет назад' },
    { start: 2000, end: currentYear, label: 'С 2000 года' }
  ];
  
  // Предопределенные ставки инфляции для быстрого выбора (если включен пользовательский режим)
  const predefinedRates = [
    { value: '4', label: '4%' },
    { value: '7', label: '7%' },
    { value: '10', label: '10%' },
    { value: '15', label: '15%' }
  ];
  
  // Функция расчета
  const calculateInflation = () => {
    // Эмулируем нажатие кнопки отправки формы
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-red-500" />
        Быстрый расчет инфляции
      </h3>
      
      <div className="space-y-5">
        {/* Сумма */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="amountSlider" className="text-gray-600 dark:text-gray-400">Сумма</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(amount)}</span>
          </div>
          <input
            type="range"
            id="amountSlider"
            min="1000"
            max="10000000"
            step="1000"
            value={amount}
            onChange={handleAmountChange}
            aria-label="Сумма"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedAmounts.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, amount: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.amount === amount.value 
                    ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Тип расчета */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Тип расчета
          </div>
          <div className="grid grid-cols-1 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, calculationType: 'depreciation' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.calculationType === 'depreciation' 
                  ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Обесценивание денег</div>
              <div className="text-xs opacity-75">Расчет снижения покупательной способности</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, calculationType: 'appreciation' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.calculationType === 'appreciation' 
                  ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Рост стоимости</div>
              <div className="text-xs opacity-75">Расчет удорожания из-за инфляции</div>
            </button>
          </div>
        </div>
        
        {/* Период расчета */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Быстрый выбор периода
          </div>
          <div className="grid grid-cols-2 gap-2">
            {predefinedPeriods.map((period, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  startYear: period.start,
                  endYear: period.end
                }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.startYear === period.start && formData.endYear === period.end
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Расширенный выбор периода */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Выбор периода вручную
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="startYearQuick" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Начальный год
              </label>
              <input
                type="number"
                id="startYearQuick"
                min={minYear}
                max={currentYear}
                value={formData.startYear}
                onChange={handleStartYearChange}
                className="form-input text-sm py-1"
              />
            </div>
            <div>
              <label htmlFor="endYearQuick" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Конечный год
              </label>
              <input
                type="number"
                id="endYearQuick"
                min={minYear}
                max={currentYear + 30}
                value={formData.endYear}
                onChange={handleEndYearChange}
                className="form-input text-sm py-1"
              />
            </div>
          </div>
        </div>
        
        {/* Пользовательская ставка инфляции (если включена) */}
        {formData.showCustomRate && (
          <div>
            <div className="flex justify-between items-center mb-1 text-sm">
              <label htmlFor="customRateSlider" className="text-gray-600 dark:text-gray-400">Ставка инфляции</label>
              <span className="font-medium text-gray-900 dark:text-white">{formData.customInflationRate}%</span>
            </div>
            <input
              type="range"
              id="customRateSlider"
              min="0"
              max="50"
              step="0.5"
              value={formData.customInflationRate}
              onChange={handleCustomRateChange}
              aria-label="Ставка инфляции (%)"
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-600"
            />
            
            {/* Предопределенные ставки инфляции */}
            <div className="grid grid-cols-4 gap-1 mt-2">
              {predefinedRates.map(rate => (
                <button
                  key={rate.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, customInflationRate: rate.value }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.customInflationRate === rate.value 
                      ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  {rate.label}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Категория товаров */}
        <div>
          <label htmlFor="itemCategoryQuick" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Категория товаров
          </label>
          <select
            id="itemCategoryQuick"
            value={formData.itemCategory}
            onChange={(e) => setFormData(prev => ({ ...prev, itemCategory: e.target.value }))}
            className="form-select"
          >
            <option value="general">Общая инфляция</option>
            <option value="food">Продукты питания</option>
            <option value="housing">Жилье и коммунальные услуги</option>
            <option value="transport">Транспорт</option>
            <option value="medical">Медицинские услуги</option>
            <option value="education">Образование</option>
            <option value="clothing">Одежда и обувь</option>
          </select>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateInflation}
          icon={<TrendingUp className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать инфляцию
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;